import { useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AuthProvider } from "@tellescope/tanstack-query";
import { getApiURL } from "../../config/api";

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider = ({ children }: AdminAuthProviderProps) => {
  const navigate = useNavigate();
  // Tracks whether we're in a pending MFA flow. Set synchronously in the login
  // mutation's onSuccess callback (before invalidateAuthQueries fires), and cleared
  // when MFA completes or is cancelled via resetAuth/logout.
  // Returning true from handleUnauthenticated suppresses the SDK's clearState() call,
  // which would otherwise wipe the session and bounce the user back to login.
  const requiresMFARef = useRef(false);

  return (
    <AuthProvider
      options={{
        sessionOptions: {
          host: getApiURL(),
          handleUnauthenticated: async () => {
            if (requiresMFARef.current) return true;
            navigate("/admin/login");
          },
        },
        loginMutationOptions: {
          onSuccess: (result) => {
            requiresMFARef.current = result.requiresMFA ?? false;
          },
        },
        logoutMutationOptions: {
          onSuccess: () => {
            requiresMFARef.current = false;
          },
        },
      }}
    >
      {children}
    </AuthProvider>
  );
};
